import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { passwordFormSelector } from 'selectors'
import { Redirect } from 'react-router-dom'
import { resetPassword } from 'actions/auth'
import Panel, { PanelHeading, PanelSubheading } from 'components/layout/Panel'
import Form, { baseError } from 'components/form'
import Input from 'components/form/Input'
import { required, exactly } from 'components/form/validations'
import Spacer from 'components/misc/Spacer'
import uniqueId from 'lodash/uniqueId'
import { LinkButton } from 'components/layout/buttons'
import PasswordInput from 'components/form/PasswordInput'
import { LocalizedRichText, LocalizedText, useLocalization } from 'localization'

const ResetPasswordForm = ({ temporarySentTo, gotoAfter }) => {
  const { t } = useLocalization()
  const dispatch = useDispatch()
  const { profile, isWebview, isLocked } = useSelector(passwordFormSelector)
  const [formKey, setFormKey] = useState(() => uniqueId())
  const [isComplete, setIsComplete] = useState(false)
  const [newPassword, setNewPassword] = useState(undefined)

  const handleData = (data) => {
    return dispatch(resetPassword(data)).then(
      () => {
        if (isWebview) {
          dispatch({
            type: 'PUSH_FLASH',
            flash: { messageKey: 'dweb_flash_password_has_been_reset', type: 'success' },
          })
          setFormKey(uniqueId())
        } else {
          setIsComplete(true)
        }
      },
      ({ response: { data } }) => {
        if (data.errors && data.errors.length) {
          return baseError(data.errors[0])
        } else {
          return baseError(
            t('dweb_form_reset_password_incorrect_password_try_again', {
              passwordType: temporarySentTo
                ? t('dweb_form_reset_password_temporary')
                : t('dweb_form_reset_password_current'),
            })
          )
        }
      }
    )
  }

  const handleChange = (event) => {
    setNewPassword(event.target.value)
  }

  if (isComplete) {
    const pathname = profile ? gotoAfter : '/sign-in'
    return (
      <Redirect
        push
        to={{
          pathname,
          state: {
            flash: { messageKey: 'dweb_flash_password_has_been_reset', type: 'success' },
          },
        }}
      />
    )
  }

  const email = temporarySentTo || profile.email
  const isUserResettingPassword = !!temporarySentTo

  return (
    <Form id="reset-password-form" key={formKey} onData={handleData}>
      {isUserResettingPassword ? (
        <PasswordForm
          heading={<LocalizedText translationKey="dweb_form_reset_password" />}
          subheading={
            <LocalizedRichText
              translationKey="dweb_form_reset_password_temporary_password_emailed"
              params={{ email: <strong>{email}</strong> }}
            />
          }
          currentPasswordLabelKey="dweb_form_reset_password_temporary_password"
          submitLabelKey="dweb_common_reset"
          profile={profile}
          email={email}
          newPassword={newPassword}
          onChange={handleChange}
          isLocked={isLocked}
        />
      ) : (
        <PasswordForm
          heading={<LocalizedText translationKey="dweb_form_reset_password_change_password" />}
          subheading={
            <LocalizedText translationKey="dweb_form_reset_password_passwords_case_sensitive_will_changed_immediately" />
          }
          currentPasswordLabelKey="dweb_form_reset_password_current_password"
          submitLabelKey="dweb_common_save"
          profile={profile}
          email={email}
          newPassword={newPassword}
          onChange={handleChange}
          isLocked={isLocked}
        />
      )}
    </Form>
  )
}

const PasswordForm = (props) => {
  const { t } = useLocalization()
  const {
    heading,
    subheading,
    currentPasswordLabelKey,
    submitLabelKey,
    profile,
    email,
    newPassword,
    onChange,
    isLocked,
  } = props
  const mustMatch = exactly(newPassword).withMessage(t('dweb_validation_passwords_do_not_match'))

  return (
    <Panel>
      <PanelHeading>{heading}</PanelHeading>
      <PanelSubheading>{subheading}</PanelSubheading>

      <Input
        password
        className="fs-block"
        label={t(currentPasswordLabelKey)}
        name="old_password"
        validate={required}
        disabled={isLocked}
      />
      <PasswordInput
        className="fs-block"
        label={t('dweb_form_reset_password_new_password')}
        name="new_password"
        onChange={onChange}
        disabled={isLocked}
      />
      <Input
        password
        className="fs-block"
        label={t('dweb_form_reset_password_confirm_new_password')}
        name="new_password_confirmation"
        validate={mustMatch.required()}
        disabled={isLocked}
      />
      <Input.Hidden name="email" value={email} />

      <Input.Submit value={t(submitLabelKey)} disabled={isLocked} />
      <Spacer height="1.6rem" />
      {!profile && (
        <LinkButton id="sign-in" to="sign-in">
          <LocalizedText translationKey="dweb_form_reset_password_sign_now" />
        </LinkButton>
      )}
    </Panel>
  )
}

export default ResetPasswordForm
